import Button from '@material-ui/core/Button';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import moment from 'moment-timezone';
import React from 'react';
import {
   AccountStore,
   Component,
   IssueActions,
   IssueStore,
   LocationStore,
   PositionStore,
   SettingsStore,
   StaffMemberStore,
   TeamStore
} from '../../../client';
import { channelMessage, constants, lodash as _ } from '../../../common';
import SweetAlert from '../../../components/feedback/SweetAlert';
import { AssignIcon, CloseIssueIcon, EditIcon, ReopenIcon, TagIcon } from '../../../components/icons';
import { Page, PageContent, PageHeader } from '../../../components/ux/Page';
import IssueAssignmentDialog, { buildAssignmentWho } from './IssueAssignmentDialog';
import IssueDefinitionDialog from './IssueDefinitionDialog';
import IssueNotes from './IssueNotes';
import IssueTagsDialog from './IssueTagsDialog';
import { buildIssueLifecyclePayload, getIssueLifecycleConfig } from './issueLifecycle';
import './style.scss';

const { issueStates, issueDeadlineSourceTypes } = constants;
const { toChannelMessageContent } = channelMessage;

const formatIssueId = (noIssue) => (noIssue == null ? 'ISS-—' : `ISS-${String(noIssue).padStart(3, '0')}`);

const formatDateTime = (value, authUser) => {
   if (!value) return 'Unknown';
   const parsed = moment(value);
   if (!parsed.isValid()) return 'Unknown';

   const timezoneCode = authUser && authUser.timezoneCode;
   const locale = (authUser && (authUser.localeFormat || authUser.localeTranslate)) || undefined;
   const zoned = timezoneCode && moment.tz.zone(timezoneCode) ? parsed.tz(timezoneCode) : parsed.local();
   if (locale) zoned.locale(locale);
   return zoned.format('lll');
};

const TIMELINE_TONE_COLORS = {
   success: '#2e7d32',
   state: '#2563eb',
   assignment: '#7c3aed',
   updated: '#d97706',
   created: '#dc2626'
};

class IssuePage extends Component {
   constructor(props) {
      super(props);
      this.state = {
         ...this.state,
         activeIssueTab: 0,
         assignmentDialogOpen: false,
         unassignConfirmOpen: false,
         tagsDialogOpen: false,
         definitionDialogOpen: false,
         lifecycleConfirm: null,
         deleteNoteConfirm: null
      };
      this.stores = [IssueStore, LocationStore, SettingsStore, AccountStore, StaffMemberStore, TeamStore, PositionStore];
      this.storeKeys = [
         'currentIssue',
         'currentIssueProvisional',
         'currentIssueLoading',
         'currentIssueNotFound',
         'currentIssueError',
         'currentIssueTimeline',
         'currentIssueTimelineLoading',
         'currentIssueTimelineError',
         'currentIssueNotes',
         'currentIssueNotesLoading',
         'currentIssueNotesError',
         'currentIssueNotesChannelId',
         'currentIssueNoteDraft',
         'currentIssueOptimisticMessages',
         'currentIssueNoteSubmittingId',
         'currentIssueNoteError',
         'currentIssueNoteLastCompletedId',
         'currentIssueNoteDeleteSaving',
         'currentIssueNoteDeleteError',
         'currentIssueNoteDeleteCompletedId',
         'currentIssueAssignmentSaving',
         'currentIssueAssignmentError',
         'currentIssueAssignmentRefreshPending',
         'currentIssueAssignmentCompletedAt',
         'currentIssueTagsSaving',
         'currentIssueTagsCreating',
         'currentIssueTagsError',
         'currentIssueTagsCreateError',
         'currentIssueTagsRefreshPending',
         'currentIssueTagsCreateCompletedAt',
         'currentIssueLifecycleSaving',
         'currentIssueLifecycleError',
         'currentIssueLifecycleRefreshPending',
         'currentIssueDefinitionSaving',
         'currentIssueDefinitionError',
         'currentIssueDefinitionRefreshPending',
         'currentIssueDeadlineSaving',
         'currentIssueDeadlineError',
         'currentIssueDeadlineRefreshPending',
         'authUser',
         'locations',
         'priorities',
         'allTags',
         'staff',
         'teams',
         'positions'
      ];
   }

   componentDidMount() {
      if (super.componentDidMount) super.componentDidMount();
      this._loadIssue();
   }

   componentDidUpdate(prevProps, prevState) {
      if (super.componentDidUpdate) super.componentDidUpdate(prevProps, prevState);

      if (prevProps.match.params.noIssue !== this.props.match.params.noIssue) {
         this.setState({ activeIssueTab: 0 });
         this._loadIssue();
         return;
      }

      const previousChannelId =
         prevState && prevState.currentIssue && !prevState.currentIssueProvisional && prevState.currentIssue.channel
            ? prevState.currentIssue.channel.id
            : null;
      const currentChannelId =
         this.state.currentIssue && !this.state.currentIssueProvisional && this.state.currentIssue.channel
            ? this.state.currentIssue.channel.id
            : null;

      if (currentChannelId && String(currentChannelId) !== String(previousChannelId)) this._loadNotes(currentChannelId);

      if (
         this.state.currentIssueNoteLastCompletedId &&
         this.state.currentIssueNoteLastCompletedId !== (prevState && prevState.currentIssueNoteLastCompletedId)
      ) {
         this._loadNotes(currentChannelId || this.state.currentIssueNotesChannelId);
      }

      if (
         this.state.currentIssueNoteDeleteCompletedId &&
         this.state.currentIssueNoteDeleteCompletedId !== (prevState && prevState.currentIssueNoteDeleteCompletedId)
      ) {
         this.setState({ deleteNoteConfirm: null });
         this._loadNotes(currentChannelId || this.state.currentIssueNotesChannelId);
      }

      if (this.state.currentIssueAssignmentRefreshPending && !(prevState && prevState.currentIssueAssignmentRefreshPending)) {
         this.setState({ assignmentDialogOpen: false });
         const noIssue = this._routeIssueNo();
         if (noIssue != null) {
            IssueActions.beginGetIssueSync({ noIssue });
            IssueActions.getIssue({ noIssue });
            this._loadTimeline(noIssue);
         }
      }

      if (this.state.currentIssueTagsRefreshPending && !(prevState && prevState.currentIssueTagsRefreshPending)) {
         this.setState({ tagsDialogOpen: false });
         const noIssue = this._routeIssueNo();
         if (noIssue != null) {
            IssueActions.beginGetIssueSync({ noIssue });
            IssueActions.getIssue({ noIssue });
            this._loadTimeline(noIssue);
         }
      }

      if (
         this.state.currentIssueTagsCreateCompletedAt &&
         this.state.currentIssueTagsCreateCompletedAt !== (prevState && prevState.currentIssueTagsCreateCompletedAt)
      ) {
         this.setState({ tagsDialogOpen: false });
         const noIssue = this._routeIssueNo();
         if (noIssue != null) {
            IssueActions.beginGetIssueSync({ noIssue });
            IssueActions.getIssue({ noIssue });
            this._loadTimeline(noIssue);
         }
      }

      if (this.state.currentIssueLifecycleRefreshPending && !(prevState && prevState.currentIssueLifecycleRefreshPending)) {
         this.setState({ lifecycleConfirm: null });
         const noIssue = this._routeIssueNo();
         if (noIssue != null) {
            IssueActions.beginGetIssueSync({ noIssue });
            IssueActions.getIssue({ noIssue });
            this._loadTimeline(noIssue);
         }
      }

      if (this.state.currentIssueDefinitionRefreshPending && !(prevState && prevState.currentIssueDefinitionRefreshPending)) {
         this.setState({ definitionDialogOpen: false });
         const noIssue = this._routeIssueNo();
         if (noIssue != null) {
            IssueActions.beginGetIssueSync({ noIssue });
            IssueActions.getIssue({ noIssue });
         }
      }

      if (this.state.currentIssueDeadlineRefreshPending && !(prevState && prevState.currentIssueDeadlineRefreshPending)) {
         const noIssue = this._routeIssueNo();
         if (noIssue != null) {
            IssueActions.beginGetIssueSync({ noIssue });
            IssueActions.getIssue({ noIssue });
            this._loadTimeline(noIssue);
         }
      }
   }

   _routeIssueNo = () => {
      const value = Number(this.props.match.params.noIssue);
      return Number.isInteger(value) && value > 0 ? value : null;
   };

   _loadIssue = () => {
      const noIssue = this._routeIssueNo();
      if (noIssue == null) {
         IssueActions.resetIssueDetailSync();
         return;
      }
      IssueActions.beginGetIssueSync({ noIssue });
      IssueActions.getIssue({ noIssue });
      this._loadTimeline(noIssue);
   };

   _loadTimeline = (noIssue = this._routeIssueNo()) => {
      if (noIssue == null) return;
      IssueActions.beginGetIssueTimelineSync({ noIssue });
      IssueActions.getIssueTimeline({ noIssue, limit: 20 });
   };

   _loadNotes = (idChannel = this.state.currentIssueNotesChannelId) => {
      if (!idChannel) return;
      IssueActions.beginGetIssueNotesSync({ idChannel });
      IssueActions.getIssueNotes({ idChannel });
   };

   _setIssueNoteDraft = (draft) => {
      IssueActions.setIssueNoteDraftSync({ draft });
   };

   _submitIssueNote = (message) => {
      const issue = this.state.currentIssue;
      const idChannel = issue && issue.channel ? issue.channel.id : null;
      if (!issue || !idChannel || !message || !message._id || this.state.currentIssueNoteSubmittingId) return;

      IssueActions.beginAddIssueCommentSync({ message });
      IssueActions.addIssueComment({
         idIssue: issue.id,
         noIssue: issue.noIssue,
         idChannel,
         idFrame: message._id,
         comment: toChannelMessageContent(message)
      });
   };

   _retryIssueNote = (message) => {
      if (!message || !message._id) return;
      this._submitIssueNote({ ...message, pending: undefined, failed: undefined });
   };

   _removeIssueNote = (message) => {
      if (!message || !message._id) return;
      IssueActions.removeIssueOptimisticMessageSync({ id: message._id });
   };

   _openDeleteNoteConfirm = (message) => {
      IssueActions.resetDeleteIssueCommentSync();
      this.setState({ deleteNoteConfirm: message });
   };

   _closeDeleteNoteConfirm = () => {
      if (this.state.currentIssueNoteDeleteSaving) return;
      IssueActions.resetDeleteIssueCommentSync();
      this.setState({ deleteNoteConfirm: null });
   };

   _confirmDeleteNote = () => {
      const issue = this.state.currentIssue;
      const message = this.state.deleteNoteConfirm;
      const idChannel = issue && issue.channel ? issue.channel.id : null;
      if (!issue || !idChannel || !message || !message.frameId || this.state.currentIssueNoteDeleteSaving) return;

      IssueActions.beginDeleteIssueCommentSync();
      IssueActions.deleteIssueComment({
         idIssue: issue.id,
         noIssue: issue.noIssue,
         idChannel,
         idFrame: message.frameId,
         utcNow: moment.utc().toDate()
      });
   };

   _openDefinition = () => {
      IssueActions.resetIssueDefinitionSync();
      this.setState({ definitionDialogOpen: true });
   };

   _closeDefinition = () => {
      if (this.state.currentIssueDefinitionSaving) return;
      IssueActions.resetIssueDefinitionSync();
      this.setState({ definitionDialogOpen: false });
   };

   _saveDefinition = ({ title, description, noPriority }) => {
      const issue = this.state.currentIssue;
      if (!issue || this.state.currentIssueDefinitionSaving) return;
      IssueActions.beginEditIssueDefinitionSync();
      IssueActions.updateIssueDefinition({
         idIssue: issue.id,
         noIssue: issue.noIssue,
         title,
         description,
         noPriority,
         utcNow: moment.utc().toDate()
      });
   };

   _resetDeadlineToPriority = () => {
      const issue = this.state.currentIssue;
      if (!issue || issue.noPriority == null || this.state.currentIssueDeadlineSaving) return;
      IssueActions.beginResetIssueDeadlineSync();
      IssueActions.resetIssueDeadline({
         idIssue: issue.id,
         noIssue: issue.noIssue,
         utcNow: moment.utc().toDate()
      });
   };

   _openAssignment = () => this.setState({ assignmentDialogOpen: true });

   _closeAssignment = () => {
      if (!this.state.currentIssueAssignmentSaving) this.setState({ assignmentDialogOpen: false });
   };

   _saveAssignment = (selection) => {
      const issue = this.state.currentIssue;
      if (!issue || this.state.currentIssueAssignmentSaving) return;
      IssueActions.beginAssignIssueSync();
      IssueActions.assignIssue({
         idIssue: issue.id,
         noIssue: issue.noIssue,
         who: buildAssignmentWho(selection)
      });
   };

   _unassignIssue = () => {
      this.setState({ unassignConfirmOpen: true });
   };

   _cancelUnassign = () => {
      this.setState({ unassignConfirmOpen: false });
   };

   _confirmUnassign = () => {
      this.setState({ unassignConfirmOpen: false });
      this._saveAssignment({ type: 'unassigned' });
   };

   _openTags = () => this.setState({ tagsDialogOpen: true });

   _closeTags = () => {
      if (!this.state.currentIssueTagsSaving && !this.state.currentIssueTagsCreating) this.setState({ tagsDialogOpen: false });
   };

   _saveTags = (selectedTags) => {
      const issue = this.state.currentIssue;
      if (!issue || this.state.currentIssueTagsSaving || this.state.currentIssueTagsCreating) return;
      IssueActions.beginManageIssueTagsSync();
      IssueActions.manageIssueTags({
         idIssue: issue.id,
         noIssue: issue.noIssue,
         selectedTags,
         availableTags: this._availableIssueTags()
      });
   };

   _createTag = (name) => {
      const issue = this.state.currentIssue;
      if (!issue || this.state.currentIssueTagsSaving || this.state.currentIssueTagsCreating) return;
      IssueActions.beginCreateIssueTagSync();
      IssueActions.createIssueTag({ idIssue: issue.id, noIssue: issue.noIssue, name: String(name || '').trim() });
   };

   _availableIssueTags = () =>
      _.filter(this.state.allTags || [], (tag) => {
         return !tag.deleted && (tag.noTagType == null || tag.noTagType === constants.tagTypes.ISSUE.id);
      });

   _openLifecycleConfirm = (action) => {
      IssueActions.resetIssueLifecycleSync();
      this.setState({ lifecycleConfirm: action });
   };

   _closeLifecycleConfirm = () => {
      if (this.state.currentIssueLifecycleSaving) return;
      IssueActions.resetIssueLifecycleSync();
      this.setState({ lifecycleConfirm: null });
   };

   _confirmLifecycle = () => {
      const issue = this.state.currentIssue;
      const action = this.state.lifecycleConfirm;
      if (!issue || !action || this.state.currentIssueLifecycleSaving) return;

      const payload = buildIssueLifecyclePayload(issue, moment.utc().toDate());

      if (action === 'close') {
         IssueActions.beginCloseIssueSync();
         IssueActions.closeIssue(payload);
      } else {
         IssueActions.beginReopenIssueSync();
         IssueActions.reopenIssue(payload);
      }
   };

   _status = (issue) => {
      const state = issue && issue.state ? issue.state : 0;
      const isClosed = (state & issueStates.CLOSED) === issueStates.CLOSED;
      const isResolved = (state & issueStates.RESOLVED) === issueStates.RESOLVED;
      const isActioned = (state & issueStates.ACTIONED) === issueStates.ACTIONED;
      const isAssigned = (state & issueStates.ASSIGNED) === issueStates.ASSIGNED;

      if (isClosed) return { caption: 'Closed', className: 'closed' };
      if (!isAssigned) return { caption: 'Unassigned', className: 'unassigned' };
      if (isResolved) return { caption: 'Resolved', className: 'resolved' };
      if (isActioned) return { caption: 'In Progress', className: 'in-progress' };
      return { caption: 'Assigned', className: 'assigned' };
   };

   _locationCaption = (issue) => {
      const noLocation = issue && issue.where ? issue.where.noLocation : null;
      const location = _.find(this.state.locations || [], (item) => String(item.no) === String(noLocation));
      return (location && (location.fullName || location.name)) || (noLocation == null ? 'No location' : `Location #${noLocation}`);
   };

   _staffCaption = (noUser) => {
      const staffMember = _.find(this.state.staff || [], (item) => String(item.noUser) === String(noUser));
      return staffMember && staffMember.user && staffMember.user.name ? staffMember.user.name : `Staff #${noUser}`;
   };

   _ownerCaption = (issue) => {
      const who = (issue && issue.who) || {};
      if (who.assignees) return who.assignees;
      if (who.caption) return who.caption;

      const captions = [];
      _.each(who.staff || [], (item) => captions.push(this._staffCaption(item.no)));
      _.each(who.teams || [], (item) => {
         const team = _.find(this.state.teams || [], (candidate) => String(candidate.no) === String(item.no));
         captions.push((team && team.name) || `Team #${item.no}`);
      });
      _.each(who.positions || [], (item) => {
         const position = _.find(this.state.positions || [], (candidate) => String(candidate.no) === String(item.no));
         captions.push((position && position.name) || `Position #${item.no}`);
      });

      return captions.length ? captions.join(', ') : 'Unassigned';
   };

   _reporterCaption = (issue) => {
      const noUser = issue && issue.raisedBy ? issue.raisedBy.noUser : null;
      return noUser == null ? 'Unknown' : this._staffCaption(noUser);
   };

   _tags = (issue) =>
      _.map((issue && issue.tags) || [], (tag) => {
         const known = _.find(this.state.allTags || [], (candidate) => String(candidate.noTag || candidate.no) === String(tag.noTag));
         return { noTag: tag.noTag, name: (known && known.name) || tag.name || `Tag #${tag.noTag}` };
      });

   _flag = (issue) => {
      if (!issue || issue.noPriority == null) return null;
      return (
         _.find(this.state.priorities || [], (flag) => String(flag.no) === String(issue.noPriority)) || {
            no: issue.noPriority,
            name: `Priority #${issue.noPriority}`,
            color: '#607d8b'
         }
      );
   };

   _renderTimeline = () => {
      const { currentIssueTimeline, currentIssueTimelineLoading, currentIssueTimelineError, authUser } = this.state;
      const rows = currentIssueTimeline || [];

      return (
         <section className='issue-timeline'>
            {currentIssueTimelineLoading && <div className='issue-timeline-state'>Loading timeline…</div>}
            {!currentIssueTimelineLoading && currentIssueTimelineError && (
               <div className='issue-timeline-state issue-timeline-error'>
                  <span>{currentIssueTimelineError}</span>
                  <Button variant='outlined' color='primary' onClick={() => this._loadTimeline()} disabled={currentIssueTimelineLoading}>
                     Retry
                  </Button>
               </div>
            )}
            {!currentIssueTimelineLoading && !currentIssueTimelineError && !rows.length && (
               <div className='issue-timeline-state'>No timeline events to display.</div>
            )}
            {!currentIssueTimelineLoading && !currentIssueTimelineError && rows.length > 0 && (
               <div className='issue-timeline-list'>
                  {rows.map((row, index) => {
                     const markerColor = TIMELINE_TONE_COLORS[row.tone] || '#8a96a3';
                     return (
                        <div className='issue-timeline-row' key={row.id || `${row.occurredOn || 'event'}-${index}`}>
                           <span className='issue-timeline-marker' style={{ backgroundColor: markerColor }} />
                           <div className='issue-timeline-copy'>
                              <strong>{row.detail || row.title || 'Timeline event'}</strong>
                              {row.detail && row.title && <span>{row.title}</span>}
                           </div>
                           <time>{formatDateTime(row.occurredOn, authUser)}</time>
                        </div>
                     );
                  })}
               </div>
            )}
         </section>
      );
   };

   _renderActivityTabs = (issue) => {
      const activeIssueTab = this.state.activeIssueTab || 0;
      const notesCount = issue && issue.channel && issue.channel.count != null ? issue.channel.count : 0;
      const timelineCount = (this.state.currentIssueTimeline || []).length;
      const idChannel = issue && issue.channel ? issue.channel.id : null;
      const isClosed = Boolean(issue && (issue.state & issueStates.CLOSED) === issueStates.CLOSED);

      return (
         <section className='issue-activity-panel'>
            <Tabs
               value={activeIssueTab}
               onChange={(event, value) => this.setState({ activeIssueTab: value })}
               indicatorColor='primary'
               textColor='primary'>
               <Tab label={`Notes · ${notesCount}`} />
               <Tab label={`Timeline · ${timelineCount}`} />
            </Tabs>
            <div className='issue-activity-content'>
               {activeIssueTab === 0 && (
                  <IssueNotes
                     channel={this.state.currentIssueNotes}
                     optimisticMessages={this.state.currentIssueOptimisticMessages}
                     loading={this.state.currentIssueNotesLoading}
                     error={this.state.currentIssueNotesError}
                     unavailable={!idChannel}
                     onRetry={() => this._loadNotes(idChannel)}
                     formatDateTime={(value) => formatDateTime(value, this.state.authUser)}
                     currentUser={this.state.authUser}
                     draft={this.state.currentIssueNoteDraft}
                     onDraftChange={this._setIssueNoteDraft}
                     onSend={this._submitIssueNote}
                     onRetryMessage={this._retryIssueNote}
                     onRemoveMessage={this._removeIssueNote}
                     submitting={Boolean(this.state.currentIssueNoteSubmittingId)}
                     submitError={this.state.currentIssueNoteError}
                     readOnly={isClosed}
                     readOnlyMessage='Closed issues cannot receive new notes.'
                     onDeleteNote={this._openDeleteNoteConfirm}
                  />
               )}
               {activeIssueTab === 1 && this._renderTimeline()}
            </div>
         </section>
      );
   };

   _renderState = ({ title, message, retry }) => (
      <div className='app-wrapper IssuePage'>
         <Page>
            <PageHeader title={title} bar />
            <PageContent>
               <div className='issue-detail-state'>
                  <h2>{message}</h2>
                  {retry && (
                     <Button variant='outlined' color='primary' onClick={this._loadIssue}>
                        Retry
                     </Button>
                  )}
               </div>
            </PageContent>
         </Page>
      </div>
   );

   render() {
      const noIssue = this._routeIssueNo();
      const { currentIssue: issue, currentIssueProvisional, currentIssueLoading, currentIssueNotFound, currentIssueError } = this.state;

      if (noIssue == null || currentIssueNotFound) {
         return this._renderState({ title: 'Issue', message: 'Issue not found' });
      }

      if (currentIssueError) {
         return this._renderState({ title: 'Issue', message: 'Unable to load issue', retry: true });
      }

      if (currentIssueLoading && !issue) {
         return this._renderState({ title: 'Issue', message: 'Loading issue…' });
      }

      if (!issue) {
         return this._renderState({ title: 'Issue', message: 'Issue not found' });
      }

      const status = this._status(issue);
      const tags = this._tags(issue);
      const flag = this._flag(issue);
      const raised = issue.raisedOn || issue.ctd;
      const updated = issue.upd || (issue.timing && issue.timing.actionedOn) || issue.ctd;
      const dueBy = issue.timing ? issue.timing.dueBy : issue.dueBy;
      const noDueBySource = issue.timing ? issue.timing.noDueBySource : issue.noDueBySource;
      const deadlineSourceCaption =
         noDueBySource === issueDeadlineSourceTypes.PRIORITY.id
            ? 'From Priority'
            : noDueBySource === issueDeadlineSourceTypes.MANUAL.id
              ? 'Manual'
              : null;
      const canResetDeadline = noDueBySource === issueDeadlineSourceTypes.MANUAL.id && issue.noPriority != null;
      const isClosed = Boolean((issue.state & issueStates.CLOSED) === issueStates.CLOSED);
      const isAssigned = Boolean((issue.state & issueStates.ASSIGNED) === issueStates.ASSIGNED);
      const lifecycleConfig = getIssueLifecycleConfig(isClosed);
      const confirmationConfig = getIssueLifecycleConfig(this.state.lifecycleConfirm === 'reopen');
      const actions = isClosed
         ? [
              {
                 icon: <ReopenIcon />,
                 name: lifecycleConfig.menuLabel,
                 onClick: () => this._openLifecycleConfirm(lifecycleConfig.action)
              }
           ]
         : [
              {
                 icon: <EditIcon />,
                 name: 'Edit issue',
                 onClick: this._openDefinition
              },
              {
                 icon: <AssignIcon />,
                 name: isAssigned ? 'Reassign issue' : 'Assign issue',
                 onClick: this._openAssignment
              },
              {
                 icon: <TagIcon />,
                 name: 'Manage tags',
                 onClick: this._openTags
              },
              {
                 icon: <CloseIssueIcon />,
                 name: lifecycleConfig.menuLabel,
                 onClick: () => this._openLifecycleConfirm(lifecycleConfig.action)
              }
           ];

      return (
         <div className='app-wrapper IssuePage'>
            <Page>
               <PageHeader title={issue.title || 'Untitled issue'} actions={actions} bar />
               <IssueDefinitionDialog
                  open={this.state.definitionDialogOpen}
                  issue={issue}
                  priorities={this.state.priorities || []}
                  saving={Boolean(this.state.currentIssueDefinitionSaving)}
                  error={this.state.currentIssueDefinitionError}
                  onClose={this._closeDefinition}
                  onSave={this._saveDefinition}
               />
               <IssueAssignmentDialog
                  open={this.state.assignmentDialogOpen}
                  issue={issue}
                  staff={this.state.staff || []}
                  teams={this.state.teams || []}
                  currentCaption={this._ownerCaption(issue)}
                  saving={Boolean(this.state.currentIssueAssignmentSaving)}
                  error={this.state.currentIssueAssignmentError}
                  onClose={this._closeAssignment}
                  onSave={this._saveAssignment}
                  onUnassign={this._unassignIssue}
               />
               <SweetAlert
                  show={this.state.unassignConfirmOpen}
                  title='Unassign issue?'
                  confirmBtnText='Unassign'
                  cancelBtnText='Cancel'
                  confirmBtnBsStyle='danger'
                  onConfirm={this._confirmUnassign}
                  onCancel={this._cancelUnassign}>
                  This issue will no longer be assigned to the current staff member or team.
               </SweetAlert>
               <SweetAlert
                  show={Boolean(this.state.deleteNoteConfirm)}
                  title='Delete this note?'
                  confirmBtnText={this.state.currentIssueNoteDeleteSaving ? 'Deleting…' : 'Delete note'}
                  cancelBtnText='Cancel'
                  confirmBtnBsStyle='danger'
                  confirmDisabled={Boolean(this.state.currentIssueNoteDeleteSaving)}
                  cancelDisabled={Boolean(this.state.currentIssueNoteDeleteSaving)}
                  onConfirm={this._confirmDeleteNote}
                  onCancel={this._closeDeleteNoteConfirm}>
                  {this.state.currentIssueNoteDeleteError && (
                     <div className='issue-note-delete-error'>{this.state.currentIssueNoteDeleteError}</div>
                  )}
               </SweetAlert>
               <SweetAlert
                  show={Boolean(this.state.lifecycleConfirm)}
                  title={confirmationConfig.title}
                  confirmBtnText={this.state.currentIssueLifecycleSaving ? confirmationConfig.busyLabel : confirmationConfig.confirmLabel}
                  cancelBtnText='Cancel'
                  confirmBtnBsStyle='primary'
                  confirmDisabled={Boolean(this.state.currentIssueLifecycleSaving)}
                  cancelDisabled={Boolean(this.state.currentIssueLifecycleSaving)}
                  onConfirm={this._confirmLifecycle}
                  onCancel={this._closeLifecycleConfirm}>
                  <div>{confirmationConfig.description}</div>
                  {this.state.currentIssueLifecycleError && (
                     <div className='issue-lifecycle-error'>{this.state.currentIssueLifecycleError}</div>
                  )}
               </SweetAlert>
               <IssueTagsDialog
                  open={this.state.tagsDialogOpen}
                  issue={issue}
                  tags={this._availableIssueTags()}
                  saving={Boolean(this.state.currentIssueTagsSaving)}
                  creating={Boolean(this.state.currentIssueTagsCreating)}
                  error={this.state.currentIssueTagsError}
                  createError={this.state.currentIssueTagsCreateError}
                  createCompletedAt={this.state.currentIssueTagsCreateCompletedAt}
                  onClose={this._closeTags}
                  onSave={this._saveTags}
                  onCreate={this._createTag}
               />
               <PageContent>
                  <div className='issue-detail-shell'>
                     {currentIssueLoading && currentIssueProvisional && (
                        <div className='issue-detail-refreshing'>Loading latest issue…</div>
                     )}
                     {!this.state.lifecycleConfirm && this.state.currentIssueLifecycleError && (
                        <div className='issue-lifecycle-refresh-error'>{this.state.currentIssueLifecycleError}</div>
                     )}
                     {!this.state.definitionDialogOpen && this.state.currentIssueDefinitionError && (
                        <div className='issue-definition-refresh-error'>{this.state.currentIssueDefinitionError}</div>
                     )}

                     <section className='issue-detail-overview'>
                        <div className='issue-detail-identity'>
                           <span className='issue-detail-id'>{formatIssueId(issue.noIssue)}</span>
                           <span className={`issue-detail-status ${status.className}`}>{status.caption}</span>
                        </div>

                        <div className={`issue-detail-description${issue.description ? '' : ' muted'}`}>
                           {issue.description || 'No description'}
                        </div>

                        <div className='issue-detail-label'>Tags</div>
                        <div className='issue-detail-tags'>
                           {tags.length ? (
                              tags.map((tag) => (
                                 <span className='issue-detail-tag' key={tag.noTag}>
                                    {tag.name}
                                 </span>
                              ))
                           ) : (
                              <span className='muted'>No tags</span>
                           )}
                        </div>

                        <div className='issue-detail-label'>Priority</div>
                        <div>
                           {flag ? (
                              <span className='issue-detail-flag' style={{ borderColor: flag.color || '#607d8b' }}>
                                 {flag.name}
                              </span>
                           ) : (
                              <span className='muted'>No priority</span>
                           )}
                        </div>
                     </section>

                     <section className='issue-detail-metadata'>
                        <div className='issue-detail-field'>
                           <span>Location</span>
                           <strong>{this._locationCaption(issue)}</strong>
                        </div>
                        <div className='issue-detail-field'>
                           <span>Owner</span>
                           <strong>{this._ownerCaption(issue)}</strong>
                        </div>
                        <div className='issue-detail-field'>
                           <span>Reporter</span>
                           <strong>{this._reporterCaption(issue)}</strong>
                        </div>
                        <div className='issue-detail-field'>
                           <span>Raised</span>
                           <strong>{formatDateTime(raised, this.state.authUser)}</strong>
                        </div>
                        <div className='issue-detail-field'>
                           <span>Updated</span>
                           <strong>{formatDateTime(updated, this.state.authUser)}</strong>
                        </div>
                        <div className='issue-detail-field issue-detail-deadline'>
                           <span>Deadline</span>
                           <strong>{dueBy ? formatDateTime(dueBy, this.state.authUser) : 'No deadline'}</strong>
                           {deadlineSourceCaption && <small>{deadlineSourceCaption}</small>}
                           {canResetDeadline && (
                              <Button
                                 size='small'
                                 color='primary'
                                 disabled={Boolean(this.state.currentIssueDeadlineSaving)}
                                 onClick={this._resetDeadlineToPriority}>
                                 {this.state.currentIssueDeadlineSaving ? 'Resetting…' : 'Reset to Priority'}
                              </Button>
                           )}
                           {this.state.currentIssueDeadlineError && (
                              <small className='issue-deadline-error'>{this.state.currentIssueDeadlineError}</small>
                           )}
                        </div>
                        <div className='issue-detail-field'>
                           <span>Issue number</span>
                           <strong>{issue.noIssue == null ? 'Unknown' : issue.noIssue}</strong>
                        </div>
                     </section>

                     {this._renderActivityTabs(issue)}
                  </div>
               </PageContent>
            </Page>
         </div>
      );
   }
}

export default IssuePage;
